import React, { forwardRef } from 'react'

import { ResourcePermissionGate } from '@/components/common/PermissionGate/ResourcePermissionGate'
import type { OrgPermissionGateProps } from '@/components/common/PermissionGate/types'
import {
  cloneElementWithDisabled,
  cloneElementWithReadOnly,
} from '@/components/common/PermissionGate/utils'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { UUID } from '@/types/Id'

/**
 * Higher-Order Component for organization-level permission checks.
 * Automatically uses the active organization ID and wraps ResourcePermissionGate.
 *
 * @example
 * // Hide create button for users without write permission
 * <OrgPermissionGate permission="write" fallback="hide">
 *   <Button>Create Pursuit</Button>
 * </OrgPermissionGate>
 *
 * @example
 * // Disable with tooltip
 * <OrgPermissionGate
 *   permission="write"
 *   fallback="disable"
 *   tooltip="You need write access to create pursuits"
 *   asChild
 * >
 *   <DropdownMenuItem>Create</DropdownMenuItem>
 * </OrgPermissionGate>
 */
export const OrgPermissionGate = forwardRef<
  HTMLElement,
  OrgPermissionGateProps
>((props, ref) => {
  const { activeOrgId } = useActiveOrgId()
  const { fallback = 'hide', loadingComponent = null, children } = props

  // If no active org, apply fallback behavior (can't check permissions without ID)
  // This prevents accidental exposure of gated content during loading or errors
  if (!activeOrgId) {
    switch (fallback) {
      case 'disable':
        return React.isValidElement(children) ? (
          cloneElementWithDisabled(children)
        ) : (
          <>{children}</>
        )
      case 'readonly':
        return React.isValidElement(children) ? (
          cloneElementWithReadOnly(children)
        ) : (
          <>{children}</>
        )
      default:
        return loadingComponent
    }
  }

  // Simply wrap ResourcePermissionGate with organization context
  return (
    <ResourcePermissionGate
      {...props}
      ref={ref}
      resourceType="organization"
      resourceId={activeOrgId as UUID}
    />
  )
})

OrgPermissionGate.displayName = 'OrgPermissionGate'
